import React from 'react';
import { Box, CircularProgress, FormControlLabel, Switch, Typography } from '@mui/material';
import { usePackageFeatureCatalog } from '../../services/package.service';
import { FormattedNumberTextField } from '../common/FormattedNumberTextField';

const text = {
  maxPrefix: 'Max',
  perCycle: 'per billing cycle',
  perDay: 'per day (UTC)',
  unlimitedHint: 'Leave empty for unlimited',
} as const;

interface PackageFeatureTogglesProps {
  value: string[];
  onChange: (next: string[]) => void;
  /** Usage limit per feature code; null/absent = unlimited. */
  limits?: Record<string, number | null>;
  onLimitsChange?: (next: Record<string, number | null>) => void;
  /** Per-UTC-day limit per feature code; null/absent = unlimited. */
  dailyLimits?: Record<string, number | null>;
  onDailyLimitsChange?: (next: Record<string, number | null>) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
}

export const PackageFeatureToggles: React.FC<PackageFeatureTogglesProps> = ({
  value,
  onChange,
  limits = {},
  onLimitsChange,
  dailyLimits = {},
  onDailyLimitsChange,
  errors = {},
  disabled = false,
}) => {
  const { data: features = [], isLoading, isError } = usePackageFeatureCatalog();

  const handleToggle = (code: string, enabled: boolean) => {
    const catalogCodes = new Set(features.map((feature) => feature.code));
    const unknownCodes = value.filter((item) => !catalogCodes.has(item));
    const selectedCodes = new Set(value.filter((item) => catalogCodes.has(item)));

    if (enabled) {
      selectedCodes.add(code);
    } else {
      selectedCodes.delete(code);
    }

    const selectedCatalogCodes = features
      .map((feature) => feature.code)
      .filter((item) => selectedCodes.has(item));

    onChange([...unknownCodes, ...selectedCatalogCodes]);
  };

  const handleLimitChange = (code: string, limit: number | undefined) => {
    onLimitsChange?.({ ...limits, [code]: limit ?? null });
  };

  const handleDailyLimitChange = (code: string, limit: number | undefined) => {
    onDailyLimitsChange?.({ ...dailyLimits, [code]: limit ?? null });
  };

  return (
    <Box>
      <Typography
        variant="subtitle1"
        className="mb-2 font-semibold text-gray-700 dark:text-slate-300"
      >
        Features
      </Typography>

      {isLoading ? (
        <CircularProgress size={20} />
      ) : isError ? (
        <Typography variant="caption" color="error">
          Could not load features
        </Typography>
      ) : (
        <Box className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {features.map((feature) => (
            <Box
              key={feature.code}
              className="rounded-lg border border-slate-200 p-3 dark:border-slate-700"
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={value.includes(feature.code)}
                    onChange={(event) => handleToggle(feature.code, event.target.checked)}
                    color="primary"
                    disabled={disabled}
                  />
                }
                label={feature.name}
              />
              <Typography variant="caption" className="block text-gray-500 dark:text-slate-400">
                {feature.description}
              </Typography>
              {feature.supportsLimit && onLimitsChange && value.includes(feature.code) && (
                <FormattedNumberTextField
                  size="small"
                  fullWidth
                  label={
                    feature.supportsDailyLimit
                      ? `${text.maxPrefix} ${feature.name.toLowerCase()} ${text.perCycle}`
                      : `${text.maxPrefix} ${feature.name.toLowerCase()}`
                  }
                  helperText={text.unlimitedHint}
                  value={limits[feature.code] ?? null}
                  onValueChange={(next) => handleLimitChange(feature.code, next)}
                  allowEmpty
                  disabled={disabled}
                  inputProps={{ min: 0 }}
                  className="mt-3"
                />
              )}
              {feature.supportsDailyLimit &&
                onDailyLimitsChange &&
                value.includes(feature.code) && (
                  <FormattedNumberTextField
                    size="small"
                    fullWidth
                    label={`${text.maxPrefix} ${feature.name.toLowerCase()} ${text.perDay}`}
                    error={Boolean(errors[feature.code])}
                    helperText={errors[feature.code] || text.unlimitedHint}
                    value={dailyLimits[feature.code] ?? null}
                    onValueChange={(next) => handleDailyLimitChange(feature.code, next)}
                    allowEmpty
                    disabled={disabled}
                    inputProps={{ min: 0 }}
                    className="mt-3"
                  />
                )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
